import * as React from "react";
import {
  Create,
  TextInput,
  ImageField,
  ImageInput,
  FileInput,
  FormDataConsumer,
  Toolbar,
  useNotify,
  useRedirect,
  SaveButton,
  SelectInput,
  useDataProvider,
  LinearProgress,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  DateTimeInput,
} from "react-admin";
import { SimpleForm } from "../common/SimpleForm";
import { validateRequired, validateVideoStartDate } from "../utils/Validations";
import VideoField from "../common/VideoField";
import { IMAGE_MAX_SIZE } from "../constants/FileConstants";
import { useFormContext, useFormState } from "react-hook-form";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { VIDEO_TYPES } from "../enums/VideoTypes";
import { Box } from "@mui/material";

const VideoSaveButton = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { getValues } = useFormContext();
  const dataProvider = useDataProvider();
  const { isValid, isSubmitting } = useFormState();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const {
        name,
        video,
        thumbnail,
        type,
        restaurants,
        products,
        startDate,
        endDate,
      } = getValues();
      if (
        !name ||
        !video ||
        !thumbnail ||
        (type === VIDEO_TYPES.RESTAURANT && !restaurants) ||
        (type === VIDEO_TYPES.PRODUCT && !products) ||
        (!startDate && endDate)
      )
        return;

      const { type: videoType, name: videoName } = video?.rawFile;

      const { data: draftVideo } = await dataProvider.create("videos", {
        data: {
          name,
          type,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(type === VIDEO_TYPES.RESTAURANT && { restaurants }),
          ...(type === VIDEO_TYPES.PRODUCT && { products }),
        },
      });

      const uploadLinkData = {
        id: draftVideo.id,
        type,
        contentType: videoType,
        fileName: videoName,
        ...(type === VIDEO_TYPES.RESTAURANT && { restaurants }),
        ...(type === VIDEO_TYPES.PRODUCT && { products }),
      };

      const { data } = await dataProvider.getVideoUploadLink(
        "videos",
        uploadLinkData
      );

      await dataProvider.uploadFileToS3(data.signedUrl, video, videoType);

      await dataProvider.update("videos", {
        data: {
          name,
          type,
          id: draftVideo.id,
          videoPath: data?.key,
          thumbnail,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(type === VIDEO_TYPES.RESTAURANT && { restaurants }),
          ...(type === VIDEO_TYPES.PRODUCT && { products }),
          active: true,
        },
      });

      notify(`Video creado exitosamente`, { type: "success" });
      redirect("/videos");
    } catch (error) {
      notify(
        `Ha ocurrido un error al crear el video: ${error?.body?.message}`,
        { type: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LinearProgress />;

  return (
    <SaveButton
      type="button"
      onClick={handleClick}
      disabled={isLoading || !isValid || isSubmitting}
    />
  );
};

const VideoCreateToolbar = () => (
  <Toolbar>
    <VideoSaveButton />
  </Toolbar>
);

const VideoCreate = () => (
  <Create title="Crear Video" mutationMode="pessimistic" redirect="list">
    <SimpleForm toolbar={<VideoCreateToolbar />}>
      <FormDataConsumer>
        {({ formData, ...rest }) => (
          <>
            <TextInput
              label="Nombre"
              source="name"
              validate={validateRequired}
              fullWidth
            />
            <FileInput
              source="video"
              accept={{
                "video/*": [],
              }}
              validate={validateRequired}
            >
              <VideoField source="originalUrl" title="Video" />
            </FileInput>
            <ImageInput
              source="thumbnail"
              label="Miniatura"
              accept={{
                "image/*": [],
              }}
              validate={validateRequired}
              maxSize={IMAGE_MAX_SIZE}
            >
              <ImageField source="src" title="Imagen" />
            </ImageInput>

            <SelectInput
              label="Tipo"
              source="type"
              choices={[
                { id: VIDEO_TYPES.RESTAURANT, name: "Restaurante" },
                // { id: VIDEO_TYPES.PRODUCT, name: "Producto" },
              ]}
              validate={validateRequired}
              fullWidth
            />

            {formData?.type === VIDEO_TYPES.RESTAURANT && (
              <ReferenceArrayInput
                source="restaurants"
                reference="restaurants"
                perPage={100}
                filter={{
                  active: true,
                  type: RESTAURANT_TYPES.REGULAR,
                }}
              >
                <AutocompleteArrayInput
                  filterToQuery={(searchText) => ({
                    name: searchText,
                  })}
                  label="Restaurantes"
                  helperText="Puedes elegir más de un restaurante"
                  fullWidth
                  optionText="name"
                />
              </ReferenceArrayInput>
            )}

            {/* {formData?.type === VIDEO_TYPES.PRODUCT && (
              <ArrayInput source="products" label="">
                <SimpleFormIterator disableReordering>
                  <SimpleFormIteratorItemContext.Consumer>
                    {({ index }) => (
                      <Box sx={{ flex: "1" }}>
                        <Box sx={{ display: "flex" }}>
                          <ReferenceInput
                            source={`restaurantProduct[${index}]`}
                            reference="restaurants"
                            perPage={100}
                            filter={{
                              active: true,
                              type: RESTAURANT_TYPES.REGULAR,
                            }}
                            fullWidth
                          >
                            <AutocompleteInput
                              filterToQuery={(searchText) => ({
                                name: searchText,
                              })}
                              label="Restaurante"
                              optionText="name"
                              fullWidth
                            />
                          </ReferenceInput>

                          <Spacer />

                          <ReferenceInput
                            source={`products[${index}]`}
                            reference="products"
                            perPage={1000}
                            sort={{ field: "name", order: "ASC" }}
                            filter={{
                              restaurant: formData?.restaurantProduct?.[index],
                              active: true,
                            }}
                            fullWidth
                          >
                            <SelectInput
                              optionText="name"
                              label="Producto"
                              disabled={!formData?.restaurantProduct?.[index]}
                              fullWidth
                            />
                          </ReferenceInput>
                        </Box>
                      </Box>
                    )}
                  </SimpleFormIteratorItemContext.Consumer>
                </SimpleFormIterator>
              </ArrayInput>
            )} */}
          </>
        )}
      </FormDataConsumer>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <DateTimeInput
              source="startDate"
              label="Fecha Inicio"
              parse={(v) => v && new Date(v).toISOString()}
              validate={validateVideoStartDate}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <DateTimeInput
              source="endDate"
              label="Fecha Fin"
              parse={(v) => v && new Date(v).toISOString()}
              fullWidth
            />
          </Box>
        </Box>
      </Box>
    </SimpleForm>
  </Create>
);

export default VideoCreate;
